import { queryOptions } from "@tanstack/react-query";
import { client } from "@/api_client/client.gen";
import type { ProductType } from "@/api_server";
import type { Locales } from "@/graphql/generated";
import {
  sanitizeDuplicateLanguageInUrl,
  sanitizeDuplicateSlashesInUrl,
} from "@/utils/formatter";
import type { EntryContentByProductType } from "../../contentTypes/entry/generalEntryTypes";

type EntryContentExternalQueryParams<P extends ProductType> = {
  product: P;
  language: Locales;
  appPathPrefix?: string;
};

const buildEntryContentProxyUrl = ({
  product,
  language,
  appPathPrefix = "",
}: EntryContentExternalQueryParams<ProductType>) => {
  const url = `${appPathPrefix}/api/cms/entry/${language}/${product}`;
  const sanitizedLanguageUrl = sanitizeDuplicateLanguageInUrl(url, language);
  return sanitizeDuplicateSlashesInUrl(sanitizedLanguageUrl);
};

export const entryContentExternalQueryOptions = <P extends ProductType>({
  product,
  language,
  appPathPrefix,
}: EntryContentExternalQueryParams<P>) =>
  queryOptions({
    queryKey: ["entry-content-external", product, language],
    queryFn: async (): Promise<EntryContentByProductType<P>> => {
      const response = await client.get<EntryContentByProductType<P>>({
        url: buildEntryContentProxyUrl({
          product,
          language,
          appPathPrefix,
        }),
        headers: {
          Accept: "application/json",
        },
      });

      const result =
        (response as { data?: EntryContentByProductType<P> }).data ??
        (response as EntryContentByProductType<P> | undefined);

      if (!result) {
        throw new Error("Entry content response missing data");
      }

      return result;
    },
    staleTime: 30 * 60 * 1000,
  });
